import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { dashboardApi } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { StatCard } from '../components/StatCard';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { formatDate, formatPeso, formatNumber } from '../utils/format';

export function DashboardPage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: dashboardApi.get,
    refetchInterval: 60_000,
  });

  const stats = data?.statistics;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Live operational overview of clients, collections and outstanding balances."
      />

      {isError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error instanceof Error ? error.message : 'Dashboard statistics could not be loaded.'}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Pending Client Leads"
            value={formatNumber(stats?.pendingClients)}
            icon={<AccessTimeOutlinedIcon />}
            color="warning"
            to="/clients?status=PENDING"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Active Clients"
            value={formatNumber(stats?.activeClients)}
            icon={<CheckCircleOutlineIcon />}
            color="success"
            to="/clients?status=ACTIVE"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Overdue Clients"
            value={formatNumber(stats?.overdueClients)}
            icon={<ReportProblemOutlinedIcon />}
            color="error"
            hint="Past due with a balance"
            to="/clients?status=OVERDUE"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Suspended Clients"
            value={formatNumber(stats?.suspendedClients)}
            icon={<PauseCircleOutlineIcon />}
            color="warning"
            to="/clients?status=SUSPENDED"
            loading={isPending}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Inactive Clients"
            value={formatNumber(stats?.inactiveClients)}
            icon={<PowerSettingsNewOutlinedIcon />}
            color="secondary"
            to="/clients?status=INACTIVE"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="New Clients This Month"
            value={formatNumber(stats?.newClientsThisMonth)}
            icon={<PersonAddAlt1OutlinedIcon />}
            color="info"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Monthly Collections"
            value={formatPeso(stats?.monthlyCollections)}
            icon={<SavingsOutlinedIcon />}
            color="success"
            hint="Payments received this month"
            to="/payments"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Outstanding Balance"
            value={formatPeso(stats?.outstandingBalance)}
            icon={<AccountBalanceWalletOutlinedIcon />}
            color="primary"
            hint={`${formatNumber(stats?.invoicesDueThisMonth)} invoice(s) due this month`}
            to="/invoices"
            loading={isPending}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard title="Retention Rate" value={`${stats?.retentionRate ?? 0}%`} icon={<CheckCircleOutlineIcon />} color="success" hint="Active share of non-pending accounts" loading={isPending} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard title="Installation Fees" value={formatPeso(stats?.installationFees)} icon={<SavingsOutlinedIcon />} color="info" loading={isPending} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard title="Referral + Installer Fees" value={formatPeso((stats?.referralFees ?? 0) + (stats?.installerCommissions ?? 0))} icon={<AccountBalanceWalletOutlinedIcon />} color="warning" loading={isPending} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard title="Net Installation Revenue" value={formatPeso(stats?.netInstallationRevenue)} icon={<SavingsOutlinedIcon />} color="primary" loading={isPending} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Payments"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <Link component={RouterLink} to="/payments" variant="body2">
                  View all
                </Link>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {!isPending && (data?.recentPayments.length ?? 0) === 0 ? (
                <EmptyState message="No payments recorded yet." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Payment</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.recentPayments.map((payment) => (
                        <TableRow key={payment.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <span>{payment.paymentNumber}</span>
                              {payment.isVoid ? <StatusBadge status="VOID" /> : null}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Link component={RouterLink} to={`/clients/${payment.client.id}`}>
                              {payment.clientName}
                            </Link>
                          </TableCell>
                          <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                textDecoration: payment.isVoid ? 'line-through' : 'none',
                                color: payment.isVoid ? 'text.disabled' : 'inherit',
                              }}
                            >
                              {formatPeso(payment.amountReceived)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Clients"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <Link component={RouterLink} to="/clients" variant="body2">
                  View all
                </Link>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {!isPending && (data?.recentClients.length ?? 0) === 0 ? (
                <EmptyState message="No clients found." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Client Number</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.recentClients.map((client) => (
                        <TableRow key={client.id} hover>
                          <TableCell>{client.clientNumber}</TableCell>
                          <TableCell>
                            <Link component={RouterLink} to={`/clients/${client.id}`}>
                              {client.displayName}
                            </Link>
                          </TableCell>
                          <TableCell>{client.servicePlanName ?? '—'}</TableCell>
                          <TableCell>
                            <StatusBadge status={client.subscriptionStatus} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader
              title="Overdue Invoices"
              subheader="Oldest unpaid invoices past their due date"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <Link component={RouterLink} to="/invoices?overdueOnly=true" variant="body2">
                  View all
                </Link>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {!isPending && (data?.overdueInvoices.length ?? 0) === 0 ? (
                <EmptyState
                  message="Nothing is overdue."
                  hint="Every issued invoice is settled or still within its due date."
                />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell align="right">Days Overdue</TableCell>
                        <TableCell align="right">Outstanding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.overdueInvoices.map((invoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>{invoice.invoiceNumber}</TableCell>
                          <TableCell>
                            <Link component={RouterLink} to={`/clients/${invoice.clientId}`}>
                              {invoice.clientName}
                            </Link>
                          </TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell align="right">
                            <StatusBadge status="OVERDUE" />
                            <Typography variant="caption" sx={{ ml: 1 }}>
                              {invoice.daysOverdue}d
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatPeso(invoice.outstandingAmount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
